import type { StateClassKey } from "@tdelivr/design-tokens";

import type { BoardCell, BoardRow } from "./board.ts";

/**
 * What the four analytics boards do to DAT-04's projections, away from any rendering.
 *
 * **These four pages have no design file.** Document 00 §19.25 asks for five kinds of analytics by
 * name, the design commission did not cover them, and they were built at the client's direction on
 * 17 October. Q-186 in `docs/design/DESIGN_QUESTIONS.md` records that, including the part that
 * cannot be waved away: every other board here can be held against a `.dc.html` and these cannot.
 *
 * So the rules followed instead, in the absence of a reference:
 *
 *  - **The nearest designed board is the pattern.** Each of these is a `RecordsBoard` with the
 *    standard toolbar, the standard empty state and the standard export, exactly as `channels` and
 *    `customers` are. Nothing new was invented at the page level.
 *  - **No chart.** Not one console on this platform draws one, so a chart here would be the first
 *    and would have no design to be faithful to. A time series as a table is honest, sorts, filters
 *    and exports to the three formats rule 4 requires — and an operator asking "how did last month
 *    go" can read it or take it to a spreadsheet.
 *  - **No tile row.** R-04 again: a tile needs a total the listing publishes, and these listings
 *    publish buckets. The freshness line below carries what a tile row would otherwise imply.
 *
 * ## Freshness is drawn, because an empty report has two meanings
 *
 * DAT-04 answers `freshestFactAt` beside every series precisely so that "nothing happened in
 * March" and "nothing has reached this service since February" can be told apart. Document 05
 * requires reports to display data freshness; `freshnessNote` below is where that requirement is
 * met, and it is the one line on these pages that must never be dropped as decoration.
 */

/** The scope all four boards spend. One scope, because they are one service's reads. */
export const ANALYTICS_READ_SCOPE = "analytics:read";

/** The widest window DAT-04 will answer, mirrored so the picker cannot offer an invalid one. */
export const MAXIMUM_RANGE_DAYS = 731;

/** The ranges the picker offers, in the words the page shows. */
export const RANGES = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "Last 12 months", days: 365 },
] as const;

export type RangeLabel = (typeof RANGES)[number]["label"];

/** The window a range means, as the two ISO instants DAT-04 takes. */
export function windowFor(label: string, now: Date): { from: string; to: string } {
  const range = RANGES.find((candidate) => candidate.label === label) ?? RANGES[0];
  const to = new Date(now);
  const from = new Date(now.getTime() - range.days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * A bucket's own label, at the granularity it was asked for.
 *
 * `en-GB` explicitly rather than the browser's locale, which is this console's existing choice
 * everywhere a date is drawn — a board whose dates change shape with the reader's machine cannot
 * be compared against anything, including itself yesterday.
 */
export function bucketLabel(iso: string, granularity: "day" | "week" | "month"): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  if (granularity === "month")
    return date.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Money as this platform writes it: the currency code and the full minor units.
 *
 * `KES 1,184,000.00`, never `KES 1.18M`. The rule is `CLAUDE.md` §2 and it is not a display
 * preference — a rounded figure on a finance-adjacent page is a figure somebody will reconcile
 * against a ledger and fail to match.
 *
 * The amount arrives as a **string** because a sum of minor units can exceed the largest integer
 * JSON carries exactly, so it is divided as a string rather than parsed to a number.
 */
export function formatMoney(amountMinor: string, currency: string): string {
  const negative = amountMinor.startsWith("-");
  const digits = (negative ? amountMinor.slice(1) : amountMinor).padStart(3, "0");
  const whole = digits.slice(0, -2);
  const cents = digits.slice(-2);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${currency} ${negative ? "-" : ""}${grouped}.${cents}`;
}

/** Several currencies, each in full, newline-joined so a cell can hold both. */
export function formatMoneyList(
  gross: readonly { readonly amountMinor: string; readonly currency: string }[],
): string {
  if (gross.length === 0) return "—";
  return gross.map((entry) => formatMoney(entry.amountMinor, entry.currency)).join("  ·  ");
}

/** Seconds as hours and minutes, which is how a shift reads. */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return "—";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return hours === 0 ? `${minutes}m` : `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

/**
 * A rate as a percentage, **or an em dash when the denominator is zero**.
 *
 * Not `0%`. A day on which no offer was sent has no acceptance rate, and writing zero there is a
 * statement that every offer was refused — which is the opposite of what happened.
 */
export function formatRate(numerator: number, denominator: number): string {
  if (denominator <= 0) return "—";
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

/**
 * The freshness line, which says what an empty board means.
 *
 * Three cases, and they are genuinely different answers to "why is this empty":
 *
 *  - no fact has **ever** reached this service for this tenant — nothing is wired yet
 *  - facts exist but none in this window — a quiet period, which is information
 *  - facts exist and the newest is old — the pipeline may have stopped, which is a problem
 */
export function freshnessNote(freshestFactAt: string | null, now: Date): string {
  if (freshestFactAt === null)
    return "No analytics facts have reached this tenant yet, so every figure here is absent rather than zero.";
  const newest = new Date(freshestFactAt);
  const hoursAgo = Math.floor((now.getTime() - newest.getTime()) / 3_600_000);
  const when =
    hoursAgo < 1
      ? "less than an hour ago"
      : hoursAgo < 24
        ? `${hoursAgo} hour${hoursAgo === 1 ? "" : "s"} ago`
        : `${Math.floor(hoursAgo / 24)} day${Math.floor(hoursAgo / 24) === 1 ? "" : "s"} ago`;
  return `Newest fact ${when}. Figures are derived from facts at read time, so they cannot disagree with the records behind them.`;
}

/**
 * A text cell, and a figure cell.
 *
 * Figures are set in Geist Mono. The design reserves it for identifiers, codes and timers, and a
 * column of numbers is the same need those serve: digits that have to line up to be compared down
 * the column. Every one of these boards is read by comparing one row against the one below it.
 */
function text(value: string): BoardCell {
  return { text: value };
}

function figure(value: string): BoardCell {
  return { text: value, mono: true };
}

/** A bucket of the demand series, as a row. */
export function demandRow(bucket: {
  readonly bucket: string;
  readonly completed: number;
  readonly cancelled: number;
  readonly gross: readonly { readonly amountMinor: string; readonly currency: string }[];
}): BoardRow {
  const total = bucket.completed + bucket.cancelled;
  return {
    id: bucket.bucket,
    cells: [
      text(bucketLabel(bucket.bucket, "day")),
      figure(String(bucket.completed)),
      figure(String(bucket.cancelled)),
      figure(formatRate(bucket.cancelled, total)),
      figure(formatMoneyList(bucket.gross)),
    ],
    state: {
      classKey: stateForCancellation(bucket.cancelled, total),
      label: `${bucket.completed} completed`,
    },
    glyph: "trending_up",
    history: [],
    detail: [
      { label: "Period", value: bucketLabel(bucket.bucket, "day") },
      { label: "Completed", value: String(bucket.completed) },
      { label: "Cancelled", value: String(bucket.cancelled) },
      { label: "Cancellation rate", value: formatRate(bucket.cancelled, total) },
      { label: "Gross", value: formatMoneyList(bucket.gross) },
    ],
  };
}

/**
 * The badge a bucket carries, by how much of it was cancelled.
 *
 * A state is never shown by colour alone here, which is rule 2's "glyph and a word" — the board
 * draws both, and these thresholds decide only which pair.
 */
function stateForCancellation(cancelled: number, total: number): StateClassKey {
  if (total === 0) return "waiting";
  const rate = cancelled / total;
  if (rate >= 0.2) return "closed";
  if (rate >= 0.1) return "waiting";
  return "done";
}

/** A bucket of the supply series, as a row. */
export function supplyRow(bucket: {
  readonly bucket: string;
  readonly onlineSeconds: number;
  readonly engagedSeconds: number;
  readonly offersSent: number;
  readonly offersAccepted: number;
  readonly activeDrivers: number;
}): BoardRow {
  const utilisation = formatRate(bucket.engagedSeconds, bucket.onlineSeconds);
  return {
    id: bucket.bucket,
    cells: [
      text(bucketLabel(bucket.bucket, "day")),
      figure(String(bucket.activeDrivers)),
      figure(formatDuration(bucket.onlineSeconds)),
      figure(formatDuration(bucket.engagedSeconds)),
      figure(utilisation),
      figure(formatRate(bucket.offersAccepted, bucket.offersSent)),
    ],
    state: {
      classKey: bucket.onlineSeconds === 0 ? "waiting" : "done",
      label: `${bucket.activeDrivers} driver${bucket.activeDrivers === 1 ? "" : "s"}`,
    },
    glyph: "two_wheeler",
    history: [],
    detail: [
      { label: "Period", value: bucketLabel(bucket.bucket, "day") },
      { label: "Active drivers", value: String(bucket.activeDrivers) },
      { label: "Online", value: formatDuration(bucket.onlineSeconds) },
      { label: "Engaged", value: formatDuration(bucket.engagedSeconds) },
      { label: "Utilisation", value: utilisation },
      { label: "Offers sent", value: String(bucket.offersSent) },
      { label: "Offers accepted", value: String(bucket.offersAccepted) },
      { label: "Acceptance", value: formatRate(bucket.offersAccepted, bucket.offersSent) },
    ],
  };
}

/** A bucket of the quality series, as a row. */
export function qualityRow(bucket: {
  readonly bucket: string;
  readonly cancelled: number;
  readonly slaBreaches: number;
  readonly supportCases: number;
  readonly satisfactionScoreTotal: number;
  readonly satisfactionRatedCount: number;
}): BoardRow {
  /*
   * The mean is computed **here**, from the total and the count DAT-04 publishes.
   *
   * ADR 0090's rule is that the service must not publish an average, because an average cannot be
   * reweighted or recombined by its reader. A screen is the reader, and it knows it is dividing
   * this bucket's total by this bucket's count — so the division is correct exactly where it is
   * made and nowhere upstream of it.
   */
  const satisfaction =
    bucket.satisfactionRatedCount === 0
      ? "—"
      : (bucket.satisfactionScoreTotal / bucket.satisfactionRatedCount).toFixed(2);
  return {
    id: bucket.bucket,
    cells: [
      text(bucketLabel(bucket.bucket, "day")),
      figure(String(bucket.cancelled)),
      figure(String(bucket.slaBreaches)),
      figure(String(bucket.supportCases)),
      figure(satisfaction),
    ],
    state: {
      classKey: bucket.slaBreaches > 0 ? "closed" : bucket.supportCases > 0 ? "waiting" : "done",
      label:
        bucket.slaBreaches > 0
          ? `${bucket.slaBreaches} breached`
          : bucket.supportCases > 0
            ? `${bucket.supportCases} case${bucket.supportCases === 1 ? "" : "s"}`
            : "clear",
    },
    glyph: "verified",
    history: [],
    detail: [
      { label: "Period", value: bucketLabel(bucket.bucket, "day") },
      { label: "Cancelled", value: String(bucket.cancelled) },
      { label: "SLA breaches", value: String(bucket.slaBreaches) },
      { label: "Support cases", value: String(bucket.supportCases) },
      { label: "Satisfaction", value: satisfaction },
      // Drawn because the mean above is meaningless without it: 5.00 from one rating and 5.00
      // from four hundred are the same number and not the same fact.
      { label: "Ratings counted", value: String(bucket.satisfactionRatedCount) },
    ],
  };
}

/** A cohort, as a row: its size, and how many returned in each of the first six months. */
export function retentionRow(cohort: {
  readonly cohort: string;
  readonly size: number;
  readonly returning: readonly { readonly periodsLater: number; readonly customers: number }[];
}): BoardRow {
  const returned = (periodsLater: number): string => {
    const match = cohort.returning.find((entry) => entry.periodsLater === periodsLater);
    if (match === undefined) return "—";
    return `${String(match.customers)} · ${formatRate(match.customers, cohort.size)}`;
  };
  return {
    id: cohort.cohort,
    cells: [
      text(bucketLabel(cohort.cohort, "month")),
      figure(String(cohort.size)),
      figure(returned(1)),
      figure(returned(2)),
      figure(returned(3)),
    ],
    state: {
      classKey: cohort.size === 0 ? "waiting" : "done",
      label: `${cohort.size} first seen`,
    },
    glyph: "group",
    history: [],
    detail: [
      { label: "Cohort", value: bucketLabel(cohort.cohort, "month") },
      { label: "Customers first seen", value: String(cohort.size) },
      ...[1, 2, 3, 4, 5, 6].map((month) => ({
        label: `Returned in month ${String(month)}`,
        value: returned(month),
      })),
    ],
  };
}

/**
 * What the retention board says about its own boundary.
 *
 * DAT-04 builds cohorts from first-seen **within the window**, not first-seen ever, and a reader
 * who does not know that will read a January cohort as "new customers in January" when it is
 * "customers whose first activity inside this window was January". Saying so is cheaper than
 * being misread, and far cheaper than the scan that "first ever" would need.
 */
export const RETENTION_NOTE =
  "Cohorts group customers by their first activity inside the selected window, not by their first ever. Widening the window moves customers between cohorts.";

/** What the demand board says about the one thing its figures are not. */
export const DEMAND_NOTE =
  "Gross is the value of completed trips and orders, before commission, refunds and adjustments. The ledger remains the authority on what was earned.";
